// Google Agenda, vu de l’application : tout passe par les fonctions serveur, avec le jeton de session.
// L’application ne détient jamais de jeton Google.

export interface GoogleStatus {
  configured: boolean;
  connected: boolean;
  email: string;
  /** Le droit d’envoyer un e-mail (gmail.send) a-t-il été accordé ? Faux pour une liaison faite avant son ajout. */
  canEmail: boolean;
}

export interface GoogleEvent {
  eventId: string;
  htmlLink: string;
  meetUrl: string | null;
}

/** Résultat du contrôle de bout en bout : jeton, renouvellement, lecture de l’agenda. */
export interface GoogleCheck {
  ok: boolean;
  /** Étape en échec : token, refresh, freebusy. */
  step?: string;
  message?: string;
  email?: string;
  /** Périodes occupées trouvées dans les 7 prochains jours. */
  busy?: number;
}

export class GoogleError extends Error {
  constructor(message: string, public readonly code: string) {
    super(message);
    this.name = 'GoogleError';
  }
}

let tokenProvider: () => Promise<string | null> = async () => null;

/** Fournit le jeton de session Supabase aux appels serveur. */
export function setGoogleTokenProvider(fn: () => Promise<string | null>): void {
  tokenProvider = fn;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await tokenProvider();
  const res = await fetch(path, {
    ...init,
    headers: { ...(init.headers as Record<string, string> | undefined), ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(init.body ? { 'content-type': 'application/json' } : {}) },
  });
  let data: { error?: string; message?: string } & T;
  try { data = (await res.json()) as typeof data; }
  catch { throw new GoogleError(`Le serveur n’a pas répondu correctement (HTTP ${res.status}).`, 'server'); }
  if (!res.ok) {
    const code = data.error ?? 'server';
    const message = data.message
      ?? (code === 'not_configured' ? 'Google Agenda n’est pas encore configuré côté serveur.'
        : code === 'not_connected' ? 'Reliez d’abord votre Google Agenda dans les préférences.'
        : code === 'unauthorized' ? 'Reconnectez-vous à l’application.'
        : `Erreur serveur (HTTP ${res.status}).`);
    throw new GoogleError(message, code);
  }
  return data;
}

/** Résultat d’un envoi d’e-mail de test : la cause exacte en cas d’échec, jamais un « ok » de façade. */
export interface GoogleMailTest { ok: boolean; step?: string; message?: string; email?: string }

export const google = {
  status: () => call<GoogleStatus>('/api/google/status'),
  check: () => call<GoogleCheck>('/api/google/check'),
  testEmail: () => call<GoogleMailTest>('/api/google/test-email', { method: 'POST', body: '{}' }),
  /** Renvoie l’adresse de consentement Google vers laquelle naviguer. */
  connect: async () => (await call<{ url: string }>('/api/google/connect', { method: 'POST', body: '{}' })).url,
  disconnect: () => call<{ ok: boolean }>('/api/google/disconnect', { method: 'POST', body: '{}' }),
  createEvent: (input: { start: number; end: number; tz: string; title: string; description?: string; attendees?: string[]; notify?: boolean }) =>
    call<GoogleEvent & { ok: boolean }>('/api/google/event', { method: 'POST', body: JSON.stringify(input) }),
  cancelBooking: (id: string) => call<{ ok: boolean; deleted?: boolean }>('/api/google/booking-cancel', { method: 'POST', body: JSON.stringify({ id }) }),
};
